import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Form, Input, InputNumber, Switch, Row, Col } from 'antd';
import { pick, capitalize } from 'lodash';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import Portlet from '../../common/components/Portlet';
import useMount from '../../common/hooks/useMount';

import {
  createEvent,
  createEventReset,
  fetchGamesForEvents,
} from '../../redux/actions/eventsActions';

import './CreateEvent.css';
import Loader from '../../common/components/Loader';
import BusyModal from '../../common/components/BusyModal';

const GAME_RESTRICTION_PREFIX = 'restriction-game-';

export const mapStateToProps = state => ({
  games: state.eventsReducer.games,
  isFetchingGames: state.eventsReducer.isFetchingGames,
  gamesError: state.eventsReducer.gamesError,
  isCreatingEvent: state.eventsReducer.isCreatingEvent,
  isCreateEventSuccess: state.eventsReducer.isCreateEventSuccess,
  isCreateEventError: state.eventsReducer.isCreateEventError,
  createEventErrorMessage: state.eventsReducer.createEventErrorMessage,
});

export const mapDispatchToProps = {
  fetchGamesForEvents,
  createEvent,
  createEventReset,
};

export const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  alreadyExists:
    stateProps.createEventErrorMessage &&
    stateProps.createEventErrorMessage.match(/^Event .* already exists.$/),
});

const portletConfig = {
  noHeader: true,
};

const formLayout = {
  labelCol: { span: 11 },
  wrapperCol: {
    span: 10,
    offset: 1,
  },
};

const initialValues = {
  status: 'inactive',
  firstTime: false,
};

export const CreateEvent = (props) => {
  const [submitted, setSubmitted] = useState(false);
  const [form] = Form.useForm();
  const [existingEvents, setExistingEvents] = useState([]);
  const [showGamesRestrictionsInfo, setShowGamesRestrictionsInfo] =
    useState(false);

  useEffect(() => {
    if (props.alreadyExists) {
      setExistingEvents([...existingEvents, form.getFieldValue('name')]);
      props.createEventReset();
    }

    if (submitted) {
      form.validateFields();
    }
  }, [props.alreadyExists, submitted]);

  useMount(() => {
    props.createEventReset();

    if (!props.games.length) {
      props.fetchGamesForEvents();
    }
  });

  useEffect(() => {
    if (props.isCreateEventSuccess) {
      props.createEventReset();
      props.history.push('/events');
    }
  }, [props.isCreateEventSuccess]);

  const handleFinish = useCallback(
    (values) => {
      console.log('handleFinish called with ', values);
      setSubmitted(true);
      const directBodyValues = pick(values, ['name', 'points', 'status']);

      const body = {
        ...directBodyValues,
        restrictionsFirstTime: !!values.firstTime,
        restrictionsGames: props.games.reduce((acc, game) => {
          const value = values[`${GAME_RESTRICTION_PREFIX}${game.gameId}`];

          if (value) {
            return [...acc, Number(game.gameId)];
          }

          return acc;
        }, []),
      };
      console.log('calling create event with', body);
      props.createEvent(body);
    },
    [props.games, props.createEvent],
  );

  const handleFieldsChange = useCallback(
    (_, values) => {
      const gameRestrictionsValues = values.filter(value =>
        value.name[0].startsWith(GAME_RESTRICTION_PREFIX));

      const allGameRestrictionsSelected = gameRestrictionsValues.every(value => value.value === true);

      if (!showGamesRestrictionsInfo && allGameRestrictionsSelected) {
        setShowGamesRestrictionsInfo(true);
      }

      if (showGamesRestrictionsInfo && !allGameRestrictionsSelected) {
        setShowGamesRestrictionsInfo(false);
      }
    },
    [showGamesRestrictionsInfo],
  );

  const handleFinishFailed = useCallback(() => {
    setSubmitted(true);
  }, []);

  return (
    <Fragment>
      <BusyModal
        isLoading={props.isCreatingEvent}
        isError={props.isCreateEventError && !props.alreadyExists}
        onClose={props.createEventReset}
      />
      <div className="row create-event">
        <div className="col-lg-12 col-xl-12 order-lg-1 order-xl-1">
          <Portlet
            config={portletConfig}
            footer={
              !props.isFetchingGames && (
              <div className="kt-portlet__foot kt-portlet__foot--lg">
                <div className="row justify-content-end">
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    to={{
                      pathname: '/events',
                    }}
                    className="mr-3 btn btn-primary"
                  >
                    CANCEL
                  </Link>
                  <button
                    onClick={form.submit}
                    type="submit"
                    className="btn btn-primary"
                  >
                    SAVE
                  </button>
                </div>
              </div>
              )
            }
          >
            <Loader isLoading={props.isFetchingGames}>
              <Form
                {...formLayout}
                initialValues={initialValues}
                validateTrigger={
                  submitted ? ['onSubmit', 'onChange'] : 'onSubmit'
                }
                form={form}
                onFinish={handleFinish}
                onFinishFailed={handleFinishFailed}
                onFieldsChange={handleFieldsChange}
              >
                <Form.Item
                  name="name"
                  label="Event Name"
                  rules={[
                    { required: true, message: 'Event Name is required' },
                    {
                      pattern: /^[a-z]+(-[a-z]+)*$/,
                      message:
                        'Event name must be comprised solely of lowercase letters and dashes',
                    },
                    {
                      max: 47,
                      message: 'Event name must be 47 characters or fewer',
                    },
                    {
                      validator: (_, val) => {
                        if (
                          props.alreadyExists ||
                          existingEvents.includes(val)
                        ) {
                          return Promise.reject(new Error('That Event Name is already in use, please choose a unique name'));
                        }

                        return Promise.resolve();
                      },
                    },
                    {
                      validator: (_, val) => {
                        if (val && val.trim().startsWith('ft-')) {
                          return Promise.reject(new Error("Event names may not start with 'ft-'"));
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    size="large"
                    onPressEnter={form.submit}
                    autoComplete="off"
                    maxLength={47}
                  />
                </Form.Item>
                <Form.Item
                  name="points"
                  label="Points"
                  rules={[
                    { required: true, message: 'Points is required' },
                    { type: 'number', message: 'Points must be a number' },
                    {
                      validator: (_, val) => {
                        const num = Number(val);

                        if (Math.abs(num) > 100) {
                          return Promise.reject(new Error('Points value can be no greater than 100 and no lesser than -100'));
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    onPressEnter={form.submit}
                    autoComplete="off"
                    className="create-event__number-input"
                    size="large"
                  />
                </Form.Item>
                <Form.Item label="Status">
                  <div className="create-event__status">
                    {capitalize(initialValues.status)}
                  </div>
                  <div
                    style={{
                      fontSize: '1rem',
                    }}
                  >
                    All new events are created in the inactive state. You must
                    edit an already created event to activate it.
                  </div>
                </Form.Item>
                <Form.Item name="status" hidden>
                  <Input size="large" autoComplete="off" disabled />
                </Form.Item>

                <Row>
                  <Col span={10}>
                    <h3 className="create-event__restrictions-header text-sm-right">
                      Restrictions
                    </h3>
                  </Col>
                </Row>

                <Form.Item
                  name="firstTime"
                  label="First Time"
                  className="create-event__game-restrictions"
                  valuePropName="checked"
                  colon={false}
                >
                  <Switch />
                </Form.Item>

                <Row>
                  <Col span={11}>
                    <h4
                      className={classNames(
                        'create-event__restrictions-header create-event__restrictions-games-header',
                        'text-sm-right mr-sm-3',
                      )}
                    >
                      Games
                    </h4>
                  </Col>
                </Row>

                {props.games.map(game => (
                  <Form.Item
                    key={game.gameId}
                    name={`${GAME_RESTRICTION_PREFIX}${game.gameId}`}
                    label={game.brandName}
                    className="create-event__game-restriction"
                    valuePropName="checked"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 9, offset: 1 }}
                    colon={false}
                  >
                    <Switch />
                  </Form.Item>
                ))}
                {showGamesRestrictionsInfo && (
                  <Row>
                    <Col xs={{ span: 24 }} sm={{ span: 9, offset: 13 }}>
                      <p>
                        Enabling all games will denote no restrictions, and act
                        the same as if all games are disabled
                      </p>
                    </Col>
                  </Row>
                )}
              </Form>
            </Loader>
          </Portlet>
        </div>
      </div>
    </Fragment>
  );
};

// export default CreateEvent;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(CreateEvent);



// WEBPACK FOOTER //
// ./src/js/views/create-event/CreateEvent.jsx